import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import {
  BRIDGE_DEVICE_DOWNLOAD_STATUS_MAP,
  BridgeDeviceDownloadStatus,
  type IBridgeDevice,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  filterUndefined,
  Firestore,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { timer } from 'rxjs';

export interface IDownloadBridgeProgressDialogData {
  deviceRef: DocumentReference<IBridgeDevice>;
}

@Component({
  selector: 'pr-download-bridge-progress-dialog',
  templateUrl: './download-bridge-progress-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatDialogModule, MatProgressBarModule, MatToolbarModule],
})
export class DownloadBridgeProgressDialogComponent {
  private _dialogRef =
    inject<MatDialogRef<DownloadBridgeProgressDialogComponent>>(MatDialogRef);
  private _data = inject<IDownloadBridgeProgressDialogData>(MAT_DIALOG_DATA);
  private _bridgeDevice$ = Firestore.doc$<IBridgeDevice>(this._data.deviceRef);

  progress = signal(0);
  progressMessage = signal('Initialising...');
  showCloseButton = signal(false);
  downloadDisabled = signal(false);

  constructor() {
    this._bridgeDevice$
      .pipe(
        map((device) => device.downloadStatus),
        filterUndefined(),
        takeUntilDestroyed()
      )
      .subscribe((status) => {
        const statusInfo = BRIDGE_DEVICE_DOWNLOAD_STATUS_MAP[status];
        this.progress.set(statusInfo?.progress ?? 0);
        this.progressMessage.set(statusInfo?.message ?? '');
        this.downloadDisabled.set(status === BridgeDeviceDownloadStatus.Failed);
        this.showCloseButton.set(
          status === BridgeDeviceDownloadStatus.Ready ||
            status === BridgeDeviceDownloadStatus.Failed
        );
      });

    timer(120_000)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (!this.showCloseButton()) {
          this.progressMessage.set('Download timed out. Please try again.');
          this.downloadDisabled.set(true);
          this.showCloseButton.set(true);
        }
      });
  }

  async download(): Promise<void> {
    const bridgeDevice: WithRef<IBridgeDevice> = await snapshot(
      this._bridgeDevice$
    );
    if (!bridgeDevice.downloadUrl) {
      return;
    }

    window.open(bridgeDevice.downloadUrl);
    this._dialogRef.close();
  }
}
